import { Badge } from "../../../../components/badge";
import { CustomModal } from "../../../../components/modal";
import { Users, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { UserItem } from "../../types/user.types";

interface UserDetailsModalProps {
  user: UserItem | null;
  onClose: () => void;
}

const roleClass = (role: string) => ({
  super_admin: "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300",
  admin: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300",
  developer: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300",
  user: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300",
}[role] ?? "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300");

const statusClass = (status: string) => status === "active"
  ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300"
  : "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300";

export function UserDetailsModal({ user, onClose }: UserDetailsModalProps) {
  const { t } = useTranslation();

  return (
    <CustomModal isOpen={Boolean(user)} onClose={onClose} maxWidth="md" title={t("users.user_details")}>
      {user ? (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <div className="col-span-1 flex flex-col gap-2">
            <Users className="mb-2 h-8 w-8 text-emerald-500" aria-hidden="true" />
            <label className="font-medium dark:text-white">{t("users.full_name")}</label>
            <div className="rounded-xl border bg-gray-50 px-3 py-2 dark:border-gray-600 dark:bg-gray-700 dark:text-white">{`${user.first_name || ""} ${user.last_name || ""}`}</div>
            <label className="mt-3 font-medium dark:text-white">{t("users.email")}</label>
            <div className="rounded-xl border bg-gray-50 px-3 py-2 dark:border-gray-600 dark:bg-gray-700 dark:text-white">{user.email}</div>
            <label className="mt-3 font-medium dark:text-white">{t("users.phone_number")}</label>
            <div className="rounded-xl border bg-gray-50 px-3 py-2 dark:border-gray-600 dark:bg-gray-700 dark:text-white">{user.phone_number || t("users.not_provided")}</div>
          </div>
          <div className="col-span-1 flex flex-col gap-2">
            <label className="font-medium dark:text-white">{t("users.role")}</label>
            <div className="rounded-xl border bg-gray-50 px-3 py-2 dark:border-gray-600 dark:bg-gray-700"><Badge className={roleClass(user.role)}>{t(`users.${user.role}`, { defaultValue: user.role })}</Badge></div>
            <label className="mt-3 font-medium dark:text-white">{t("users.status")}</label>
            <div className="rounded-xl border bg-gray-50 px-3 py-2 dark:border-gray-600 dark:bg-gray-700"><Badge className={statusClass(user.status)}>{user.status.charAt(0).toUpperCase() + user.status.slice(1)}</Badge></div>
            <label className="mt-3 font-medium dark:text-white">{t("users.email_verified")}</label>
            <div className="rounded-xl border bg-gray-50 px-3 py-2 dark:border-gray-600 dark:bg-gray-700 dark:text-white">{user.is_email_verified ? t("users.yes") : t("users.no")}</div>
          </div>
          <div>
            <label className="mb-1 block font-medium dark:text-white">{t("users.join_date")}</label>
            <div className="rounded-xl border bg-gray-50 px-3 py-2 dark:border-gray-600 dark:bg-gray-700 dark:text-white">{new Date(user.created_at).toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" })}</div>
          </div>
          <div>
            <label className="mb-1 block font-medium dark:text-white">{t("users.last_login")}</label>
            <div className="rounded-xl border bg-gray-50 px-3 py-2 dark:border-gray-600 dark:bg-gray-700 dark:text-white">{user.last_login_date ? new Date(user.last_login_date).toLocaleString() : t("users.never")}</div>
          </div>
          <div className="col-span-full flex justify-end">
            <button type="button" className="inline-flex items-center gap-2 rounded-xl bg-emerald-500 px-4 py-2 text-white" onClick={onClose}>
              <X className="h-4 w-4" aria-hidden="true" />
              {t("common.close")}
            </button>
          </div>
        </div>
      ) : null}
    </CustomModal>
  );
}
